import { useEffect, useMemo } from 'react';
import type { RevitElement, RevitFamily } from '../../../shared/api/revit/types';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import { perf } from '../../../shared/lib/perfLogger';
import type { ValidationQuickFillFocus } from '../../../shared/types/validationNavigation.types';
import {
  buildQuickFillAvailableParameterCatalog,
  buildQuickFillParameterOptions,
} from '../lib/quickFillAvailableParameterCatalog';
import type { QuickFillPipeElement } from '../lib/quickFillDataCache';
import {
  filterQuickFillElementsByFocus,
  filterQuickFillFamiliesByFocus,
} from '../lib/quickFillScopeFiltering';
import {
  buildDerivedSummary,
  buildSourceSummary,
  mapFallbackElementToQuickFillPipeElement,
  shouldUseE2eQuickFillFallback,
} from '../lib/quickFillInitializationSummary';
import type { AvailableParam } from './useQuickFillData';

const PIPE_CATEGORIES = [
  'Pipes', 'Flex Pipes', 'PipeFittings', 'PipeAccessories',
  'FlexPipes', 'FlexPipeCurves', 'Pipe Fittings', 'Pipe Accessories',
  'Generic Models', 'Mechanical Equipment',
];

interface QuickFillPayloadSelectionInput {
  grpcFamilies: RevitFamily[];
  grpcPipeElements: QuickFillPipeElement[];
  grpcAvailableParams: AvailableParam[];
  isGrpcLoaded: boolean;
  grpcError: string | null;
  fallbackElements: RevitElement[];
  fallbackFamilies: RevitFamily[];
  isFallbackLoaded: boolean;
  validationScope?: ValidationQuickFillFocus | null;
}

export function useQuickFillPayloadSelection(input: QuickFillPayloadSelectionInput) {
  const allowE2eQuickFillFallback = shouldUseE2eQuickFillFallback();
  const preferGrpcPayload = isGrpcRuntimeAvailable();
  const preferGrpcPayloadOnly = preferGrpcPayload && !allowE2eQuickFillFallback;
  const grpcSucceeded = input.isGrpcLoaded && !input.grpcError;
  const grpcReturnedEmptyPayload = grpcSucceeded && input.grpcFamilies.length === 0 &&
    input.grpcPipeElements.length === 0 && input.grpcAvailableParams.length === 0;
  const shouldUseFallbackPayload = !preferGrpcPayload || (
    !preferGrpcPayloadOnly && input.isGrpcLoaded &&
    (Boolean(input.grpcError) || (allowE2eQuickFillFallback && grpcReturnedEmptyPayload))
  );
  const activePayloadSource: 'gRPC' | 'fallback' = shouldUseFallbackPayload ? 'fallback' : 'gRPC';
  const dataSourceSettled = preferGrpcPayload
    ? (input.isGrpcLoaded || (shouldUseFallbackPayload && input.isFallbackLoaded))
    : input.isFallbackLoaded;
  const grpcHasData = grpcSucceeded && (
    input.grpcFamilies.length > 0 || input.grpcPipeElements.length > 0 ||
    input.grpcAvailableParams.length > 0
  );
  const revitFamilies = shouldUseFallbackPayload ? input.fallbackFamilies : input.grpcFamilies;
  const pipeCategorySet = useMemo(
    () => new Set([...PIPE_CATEGORIES, 'GenericModels', 'MechanicalEquipment']),
    []
  );

  if (shouldUseFallbackPayload && input.fallbackFamilies.length > 0) {
    perf.warn('QF-Init', 'Quick Fill snapshot request failed, using WebView2 RevitDataContext fallback', {
      grpcError: input.grpcError,
      grpcFamilies: input.grpcFamilies.length,
      fallbackFamilies: input.fallbackFamilies.length,
      fallbackElements: input.fallbackElements.length,
    });
  }

  const pipeFamilies = useMemo(() => {
    if (!Array.isArray(revitFamilies) || revitFamilies.length === 0) return [];
    const result = revitFamilies.filter((family) => family && PIPE_CATEGORIES.includes(family.category));
    perf.log('QF-Init', `pipeFamilies: ${result.length}/${revitFamilies.length}`, { source: activePayloadSource });
    return result;
  }, [activePayloadSource, revitFamilies]);
  const pipeElements = useMemo(() => {
    if (!shouldUseFallbackPayload && grpcSucceeded) return input.grpcPipeElements;
    if (!shouldUseFallbackPayload || input.fallbackElements.length === 0) return [];
    return input.fallbackElements
      .filter((element) => element?.category && pipeCategorySet.has(element.category))
      .map(mapFallbackElementToQuickFillPipeElement);
  }, [grpcSucceeded, input.fallbackElements, input.grpcPipeElements, pipeCategorySet, shouldUseFallbackPayload]);
  const scopedPipeElements = useMemo(
    () => filterQuickFillElementsByFocus(pipeElements, input.validationScope),
    [input.validationScope, pipeElements]
  );
  const scopedPipeFamilies = useMemo(
    () => filterQuickFillFamiliesByFocus(
      pipeFamilies,
      input.validationScope,
      scopedPipeElements,
      pipeElements.length > 0
    ),
    [input.validationScope, pipeElements.length, pipeFamilies, scopedPipeElements]
  );
  const validationScopeResolvedEmpty = Boolean(
    input.validationScope?.requestId &&
    input.validationScope.elementIds.some((elementId) => elementId.trim() !== '') &&
    dataSourceSettled && pipeElements.length > 0 && scopedPipeElements.length === 0 &&
    scopedPipeFamilies.length === 0
  );
  const availableParameters = useMemo(() => buildQuickFillAvailableParameterCatalog({
    pipeFamilies: scopedPipeFamilies,
    pipeElements: scopedPipeElements,
    grpcAvailableParameters: input.grpcAvailableParams,
  }), [input.grpcAvailableParams, scopedPipeElements, scopedPipeFamilies]);
  const sharedParametersOptions = useMemo(
    () => buildQuickFillParameterOptions(input.grpcAvailableParams),
    [input.grpcAvailableParams]
  );

  useEffect(() => {
    if (!input.validationScope?.requestId) return;
    perf.log('QF-Handoff', 'Applied validation scope to Quick Fill source payload', {
      requestId: input.validationScope.requestId,
      originalFamilies: pipeFamilies.length,
      scopedFamilies: scopedPipeFamilies.length,
      originalElements: pipeElements.length,
      scopedElements: scopedPipeElements.length,
      elementIds: input.validationScope.elementIds,
      familyTypeKeys: input.validationScope.familyTypeKeys,
    });
  }, [input.validationScope, pipeElements.length, pipeFamilies.length, scopedPipeElements.length, scopedPipeFamilies.length]);

  useEffect(() => {
    const sourceSummary = buildSourceSummary({
      activePayloadSource,
      grpcHasData,
      isGrpcLoaded: input.isGrpcLoaded,
      grpcFamilies: input.grpcFamilies,
      grpcPipeElements: input.grpcPipeElements,
      grpcAvailableParams: input.grpcAvailableParams,
      isFallbackLoaded: input.isFallbackLoaded,
      fallbackFamilies: input.fallbackFamilies,
      fallbackElements: input.fallbackElements,
    });
    perf.log('QF-Init', 'Source payload snapshot', sourceSummary);
  }, [activePayloadSource, grpcHasData, input.fallbackElements, input.fallbackFamilies, input.grpcAvailableParams, input.grpcFamilies, input.grpcPipeElements, input.isFallbackLoaded, input.isGrpcLoaded]);

  useEffect(() => {
    const derivedSummary = buildDerivedSummary({
      activePayloadSource,
      pipeFamilies: scopedPipeFamilies,
      pipeElements: scopedPipeElements,
      availableParameters,
    });
    perf.log('QF-Init', 'Derived Quick Fill payload', derivedSummary);
  }, [activePayloadSource, availableParameters, scopedPipeElements, scopedPipeFamilies]);

  return {
    allowE2eQuickFillFallback,
    preferGrpcPayload,
    preferGrpcPayloadOnly,
    activePayloadSource,
    dataSourceSettled,
    scopedPipeElements,
    scopedPipeFamilies,
    validationScopeResolvedEmpty,
    availableParameters,
    sharedParametersOptions,
  };
}
